import CheckRounded from "@mui/icons-material/CheckRounded";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const plans = [
  {
    id: "individual",
    name: "Individual",
    price: "Free Forever",
    priceNote: "",
    highlight: false,
    features: [
      "Upload up to 50 documents",
      "Basic health timeline",
      "Lab trend charts",
      "AI-generated summaries (5/month)",
      "Secure encrypted storage"
    ],
    cta: "Get Started Free",
    ctaVariant: "outline",
    href: "/register"
  },
  {
    id: "professional",
    name: "Professional",
    price: "$29",
    priceNote: "/ month",
    highlight: true,
    badge: "Most Popular",
    features: [
      "Unlimited document uploads",
      "Advanced AI insights & alerts",
      "Biomarker trend analysis",
      "Medication interaction checks",
      "Family member profiles (up to 5)",
      "EHR portal connections",
      "Priority support"
    ],
    cta: "Start Pro Trial",
    ctaVariant: "primary",
    href: "/register"
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: "Custom Pricing",
    priceNote: "",
    highlight: false,
    features: [
      "Unlimited everything",
      "Full EHR/EMR integration (Epic, Cerner)",
      "HL7 FHIR API access",
      "HIPAA BAA included",
      "Dedicated clinical success manager",
      "Custom AI model fine-tuning",
      "SLA guarantee + 24/7 support",
      "On-premise deployment option"
    ],
    cta: "Contact Sales",
    ctaVariant: "outline",
    href: "mailto:sales@healthtimeline.ai?subject=Enterprise%20inquiry"
  }
];

export default function PricingSection() {
  return (
    <section id="pricing" className="mx-auto max-w-7xl px-6 py-20 md:px-10">
      <div className="mx-auto mb-14 max-w-3xl text-center">
        <span className="inline-block rounded-full bg-sky-100 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-sky-800">
          Transparent Pricing
        </span>
        <h2 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 md:text-4xl">
          Choose the Plan That Fits Your Journey
        </h2>
        <p className="mt-4 text-lg text-gray-600">
          From individual patients to enterprise hospital networks — flexible plans with no hidden fees.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3 lg:items-stretch">
        {plans.map((plan, index) => (
          <motion.div
            key={plan.id}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.06 }}
            whileHover={{ y: plan.highlight ? -2 : -4 }}
            className={`relative flex flex-col rounded-2xl border bg-white p-6 shadow-sm md:p-8 ${
              plan.highlight
                ? "border-gray-900 shadow-lg ring-2 ring-gray-900/10 lg:scale-[1.02]"
                : "border-gray-200/90"
            }`}
          >
            {plan.badge ? (
              <div className="absolute left-0 right-0 top-0 flex justify-center">
                <span className="-translate-y-1/2 rounded-full bg-emerald-100 px-4 py-1 text-xs font-semibold text-emerald-900">
                  {plan.badge}
                </span>
              </div>
            ) : null}

            <div className={plan.badge ? "mt-4" : ""}>
              <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-3xl font-bold text-gray-900 md:text-4xl">{plan.price}</span>
                {plan.priceNote ? <span className="text-sm font-medium text-gray-500">{plan.priceNote}</span> : null}
              </div>
            </div>

            <ul className="mt-6 flex-1 space-y-3 text-sm text-gray-600">
              {plan.features.map((line) => (
                <li key={line} className="flex gap-2">
                  <CheckRounded
                    sx={{ fontSize: 20 }}
                    className={`mt-0.5 shrink-0 ${plan.highlight ? "text-emerald-600" : "text-sky-600"}`}
                  />
                  <span>{line}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8">
              {plan.href.startsWith("mailto:") ? (
                <a
                  href={plan.href}
                  className={`flex w-full items-center justify-center rounded-xl px-4 py-3 text-sm font-semibold transition ${
                    plan.ctaVariant === "primary"
                      ? "bg-gray-900 text-white hover:bg-gray-800"
                      : "border border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
                  }`}
                >
                  {plan.cta}
                </a>
              ) : (
                <Link
                  to={plan.href}
                  className={`flex w-full items-center justify-center rounded-xl px-4 py-3 text-sm font-semibold transition ${
                    plan.ctaVariant === "primary"
                      ? "bg-gray-900 text-white hover:bg-gray-800"
                      : "border border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
                  }`}
                >
                  {plan.cta}
                </Link>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      <p className="mx-auto mt-12 max-w-4xl text-center text-sm leading-relaxed text-gray-500">
        All plans include 256-bit AES encryption, HIPAA compliance, SOC 2 Type II certification, and a 30-day money-back
        guarantee.
      </p>
    </section>
  );
}
